/**
 * Organization model (plan 4.1, 4.3; FR-ORG-01, FR-ORG-02, FR-ORG-05):
 * legal entities and branches of a company (tenant), each legal entity with
 * its own default workspace, and per-user legal entity / branch / warehouse
 * scopes. There is no limit on the number of legal entities.
 *
 *   legalEntities/{id}  tenantId, code, name, country, functionalCurrency,
 *                       fiscalYearStartMonth, taxIds[], legalAddress{},
 *                       language, timezone, isPrimary, status, defaultWorkspaceId
 *   branches/{id}       tenantId, legalEntityId, code, name, address{}, timezone, status
 *   userScopes/{uid}    tenantId, userId, allLegalEntities, legalEntityIds[],
 *                       branchIds[], warehouseIds[]
 */

import { collection, doc, getDoc, getDocs, query, where, setDoc, updateDoc, serverTimestamp, deleteDoc } from "./fs";
import { db } from "../config/firebase";
import { sortRows } from "./ownership";

export const LANGUAGES = ["tr", "en", "ar", "de", "fr", "es", "ru"];
export const TAX_ID_TYPES = ["vkn", "tckn", "vat", "ein", "trn", "crn", "other"];
export const ORG_STATUSES = ["active", "inactive"];
export const LEGAL_ENTITY_WORKSPACE_PREFIX = "le_";

const ISO2 = /^[A-Z]{2}$/;
const ISO3 = /^[A-Z]{3}$/;
const CODE = /^[A-Za-z0-9._-]{1,20}$/;

export function isValidTimezone(tz) {
  if (!tz || typeof tz !== "string") return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch (_) {
    return false;
  }
}

const clean = (v) => String(v ?? "").trim();

export function normalizeAddress(a = {}) {
  return {
    line1: clean(a.line1),
    line2: clean(a.line2),
    city: clean(a.city),
    region: clean(a.region),
    postalCode: clean(a.postalCode),
    country: clean(a.country).toUpperCase()
  };
}

export function normalizeLegalEntity(input = {}) {
  const country = clean(input.country).toUpperCase();
  return {
    code: clean(input.code).toUpperCase(),
    name: clean(input.name),
    country,
    functionalCurrency: clean(input.functionalCurrency).toUpperCase(),
    fiscalYearStartMonth: Number(input.fiscalYearStartMonth) || 1,
    taxIds: (input.taxIds || [])
      .map((x) => ({ type: clean(x.type) || "other", value: clean(x.value), country: clean(x.country || country).toUpperCase() }))
      .filter((x) => x.value),
    legalAddress: normalizeAddress({ country, ...(input.legalAddress || {}) }),
    language: clean(input.language) || "tr",
    timezone: clean(input.timezone) || "Europe/Istanbul",
    status: ORG_STATUSES.includes(input.status) ? input.status : "active"
  };
}

/** First problem as an error code, or null. `existing` guards duplicate codes. */
export function validateLegalEntity(le, existing = [], selfId = null) {
  if (!le.name) return "NAME_REQUIRED";
  if (!CODE.test(le.code)) return "CODE_INVALID";
  if (existing.some((x) => x.id !== selfId && String(x.code || "").toUpperCase() === le.code)) return "CODE_DUPLICATE";
  if (!ISO2.test(le.country)) return "COUNTRY_INVALID";
  if (!ISO3.test(le.functionalCurrency)) return "CURRENCY_INVALID";
  if (!(Number.isInteger(le.fiscalYearStartMonth) && le.fiscalYearStartMonth >= 1 && le.fiscalYearStartMonth <= 12)) return "FISCAL_YEAR_INVALID";
  if (!LANGUAGES.includes(le.language)) return "LANGUAGE_INVALID";
  if (!isValidTimezone(le.timezone)) return "TIMEZONE_INVALID";
  if (le.taxIds.some((x) => !TAX_ID_TYPES.includes(x.type))) return "TAX_ID_TYPE_INVALID";
  const keys = le.taxIds.map((x) => `${x.type}:${x.value}`);
  if (new Set(keys).size !== keys.length) return "TAX_ID_DUPLICATE";
  if (!le.legalAddress.line1 || !le.legalAddress.city) return "ADDRESS_REQUIRED";
  return null;
}

export function normalizeBranch(input = {}) {
  return {
    legalEntityId: clean(input.legalEntityId),
    code: clean(input.code).toUpperCase(),
    name: clean(input.name),
    address: normalizeAddress(input.address || {}),
    timezone: clean(input.timezone),
    status: ORG_STATUSES.includes(input.status) ? input.status : "active"
  };
}

export function validateBranch(b, legalEntities = [], existing = [], selfId = null) {
  if (!b.name) return "NAME_REQUIRED";
  if (!CODE.test(b.code)) return "CODE_INVALID";
  if (!legalEntities.some((le) => le.id === b.legalEntityId)) return "LEGAL_ENTITY_REQUIRED";
  if (existing.some((x) => x.id !== selfId && x.legalEntityId === b.legalEntityId && String(x.code || "").toUpperCase() === b.code)) return "CODE_DUPLICATE";
  if (b.timezone && !isValidTimezone(b.timezone)) return "TIMEZONE_INVALID";
  return null;
}

/** First month of the fiscal year containing `date` → { start, end } as YYYY-MM-DD. */
export function fiscalYearOf(startMonth = 1, date = new Date()) {
  const m = Math.min(12, Math.max(1, Number(startMonth) || 1));
  const y = date.getUTCMonth() + 1 >= m ? date.getUTCFullYear() : date.getUTCFullYear() - 1;
  const start = new Date(Date.UTC(y, m - 1, 1));
  const end = new Date(Date.UTC(y + 1, m - 1, 0));
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
}

export const legalEntityWorkspaceId = (legalEntityId) => `${LEGAL_ENTITY_WORKSPACE_PREFIX}${legalEntityId}`;

/** The legal entity records are booked to: the workspace's, else the primary one. */
export function activeLegalEntity({ workspace = null, legalEntities = [] } = {}) {
  const active = legalEntities.filter((le) => le.status !== "inactive");
  if (workspace?.legalEntityId && active.some((le) => le.id === workspace.legalEntityId)) return workspace.legalEntityId;
  return (active.find((le) => le.isPrimary) || active[0])?.id || null;
}

export { normalizeScope, recordInScope, filterByScope } from "./orgScope";

export function validateScope(input, { legalEntities = [], branches = [], warehouses = [] } = {}) {
  if (input.allLegalEntities) return null;
  const les = input.legalEntityIds || [];
  if (!les.length) return "SCOPE_LEGAL_ENTITY_REQUIRED";
  if (les.some((id) => !legalEntities.some((le) => le.id === id))) return "SCOPE_UNKNOWN_LEGAL_ENTITY";
  if ((input.branchIds || []).some((id) => !les.includes(branches.find((b) => b.id === id)?.legalEntityId))) return "SCOPE_BRANCH_OUTSIDE";
  if ((input.warehouseIds || []).some((id) => !les.includes(warehouses.find((w) => w.id === id)?.legalEntityId))) return "SCOPE_WAREHOUSE_OUTSIDE";
  return null;
}

// ---- Services ---------------------------------------------------------------

async function listByTenant(name, tenantId) {
  if (!tenantId) return [];
  const snap = await getDocs(query(collection(db, name), where("tenantId", "==", tenantId)));
  return sortRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })), "code", "asc");
}

const newId = (name) => doc(collection(db, name)).id;

export const LegalEntityService = {
  list(tenantId) {
    return listByTenant("legalEntities", tenantId);
  },

  async get(id) {
    const snap = await getDoc(doc(db, "legalEntities", id));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  /**
   * Creates the legal entity and its default workspace. The first one becomes
   * primary and uses the company's existing `default_<tenant>` workspace.
   */
  async create(input, { tenantId, userId }) {
    const existing = await this.list(tenantId);
    const data = normalizeLegalEntity(input);
    const err = validateLegalEntity(data, existing);
    if (err) throw new Error(err);
    const id = newId("legalEntities");
    const isPrimary = !existing.some((x) => x.isPrimary);
    const defaultWorkspaceId = isPrimary ? `default_${tenantId}` : legalEntityWorkspaceId(id);
    if (!isPrimary) {
      await setDoc(doc(db, "workspaces", defaultWorkspaceId), {
        name: data.name, isDefault: true, tenantId, legalEntityId: id,
        members: [userId], createdBy: userId, createdAt: serverTimestamp(), updatedAt: serverTimestamp()
      });
    }
    const payload = {
      ...data, tenantId, isPrimary, defaultWorkspaceId,
      createdBy: userId, createdAt: serverTimestamp(), updatedAt: serverTimestamp()
    };
    await setDoc(doc(db, "legalEntities", id), payload);
    if (isPrimary) {
      // The default workspace may not exist yet; the next login creates it and
      // the 4.2 migration tags it.
      await updateDoc(doc(db, "workspaces", defaultWorkspaceId), { legalEntityId: id, updatedAt: serverTimestamp() })
        .catch(() => {});
    }
    return { id, ...payload };
  },

  async update(id, input, { tenantId, userId }) {
    const existing = await this.list(tenantId);
    const data = normalizeLegalEntity(input);
    const err = validateLegalEntity(data, existing, id);
    if (err) throw new Error(err);
    const current = existing.find((x) => x.id === id);
    if (current?.isPrimary && data.status === "inactive") throw new Error("PRIMARY_INACTIVE");
    await updateDoc(doc(db, "legalEntities", id), { ...data, updatedAt: serverTimestamp(), updatedBy: userId || null });
    return { ...current, ...data };
  },

  async setPrimary(id, { tenantId, userId }) {
    const all = await this.list(tenantId);
    const target = all.find((x) => x.id === id);
    if (!target) throw new Error("NOT_FOUND");
    if (target.status === "inactive") throw new Error("PRIMARY_INACTIVE");
    for (const le of all) {
      if (!!le.isPrimary !== (le.id === id)) {
        await updateDoc(doc(db, "legalEntities", le.id), { isPrimary: le.id === id, updatedAt: serverTimestamp(), updatedBy: userId || null });
      }
    }
  }
};

export const BranchService = {
  list(tenantId) {
    return listByTenant("branches", tenantId);
  },

  async save(input, { id = null, tenantId, userId, legalEntities = [] }) {
    const existing = await this.list(tenantId);
    const data = normalizeBranch(input);
    const err = validateBranch(data, legalEntities, existing, id);
    if (err) throw new Error(err);
    if (id) {
      await updateDoc(doc(db, "branches", id), { ...data, updatedAt: serverTimestamp(), updatedBy: userId || null });
      return { id, ...data };
    }
    const newBranchId = newId("branches");
    const payload = { ...data, tenantId, createdBy: userId, createdAt: serverTimestamp(), updatedAt: serverTimestamp() };
    await setDoc(doc(db, "branches", newBranchId), payload);
    return { id: newBranchId, ...payload };
  }
};

export const UserScopeService = {
  async get(userId) {
    if (!userId) return null;
    const snap = await getDoc(doc(db, "userScopes", userId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  async list(tenantId) {
    if (!tenantId) return [];
    const snap = await getDocs(query(collection(db, "userScopes"), where("tenantId", "==", tenantId)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  /** `allLegalEntities` (or no document) = unrestricted. */
  async save(userId, input, { tenantId, updatedBy, legalEntities, branches, warehouses }) {
    const err = validateScope(input, { legalEntities, branches, warehouses });
    if (err) throw new Error(err);
    const payload = {
      tenantId,
      userId,
      allLegalEntities: !!input.allLegalEntities,
      legalEntityIds: input.allLegalEntities ? [] : [...new Set(input.legalEntityIds || [])],
      branchIds: input.allLegalEntities ? [] : [...new Set(input.branchIds || [])],
      warehouseIds: input.allLegalEntities ? [] : [...new Set(input.warehouseIds || [])],
      updatedBy: updatedBy || null,
      updatedAt: serverTimestamp()
    };
    await setDoc(doc(db, "userScopes", userId), payload);
    return payload;
  },

  async clear(userId) {
    await deleteDoc(doc(db, "userScopes", userId));
  }
};
